import { Head, Link, router } from "@inertiajs/react";
import { Building2, ChevronLeft, Eye, Plus } from "lucide-react";
import { useState } from "react";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout";
import { CompanyLogo } from "@/components/CompanyLogo";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { GicsTreeNode } from "@/lib/industry";
import {
  bizminer_admin_organization_account_path,
  bizminer_admin_organization_accounts_path,
  bizminer_admin_organization_path,
} from "@/routes";
import type { PagyMetadata } from "@/types/Pagy";
import type {
  BizminerAdminAccountList,
  BizminerAdminOrganizationList,
  BizminerIndustry,
} from "@/types/serializers";
import { CreateAccountDialog } from "../Organizations/_components/CreateAccountDialog";

interface Props {
  accounts: BizminerAdminAccountList[];
  organization: BizminerAdminOrganizationList;
  gics_tree: GicsTreeNode[];
  industries: BizminerIndustry[];
  pagy: PagyMetadata;
}

export default function Index({
  accounts,
  organization,
  gics_tree,
  industries,
  pagy,
}: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handlePageChange = (page: number) => {
    router.get(
      bizminer_admin_organization_accounts_path(organization.id),
      { page },
      { preserveState: true, preserveScroll: true },
    );
  };

  return (
    <BizminerAdminLayout>
      <Head
        title={`Accounts - ${organization.name} - Organizations - Super Admin`}
      />

      <div className="min-h-screen bg-background">
        <div className="border-b bg-card">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link
                  href={bizminer_admin_organization_path(organization.id)}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {organization.name}
                </Link>
              </div>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Account
              </Button>
            </div>
            <h1 className="text-2xl font-semibold text-foreground mt-2">
              Accounts
            </h1>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <Card>
              <CardHeader className="border-b">
                <CardTitle className="text-lg">All Accounts</CardTitle>
                <CardDescription>
                  {pagy.count} account{pagy.count !== 1 ? "s" : ""} in this
                  organization
                </CardDescription>
              </CardHeader>

              {accounts.length === 0 ? (
                <CardContent className="text-center py-12 text-muted-foreground">
                  <Building2 className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>No accounts found for this organization</p>
                </CardContent>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableHead className="px-6">Logo</TableHead>
                        <TableHead className="px-6">Name</TableHead>
                        <TableHead className="px-6">Industry</TableHead>
                        <TableHead className="px-6 text-right">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accounts.map((account) => (
                        <TableRow key={account.id}>
                          <TableCell className="px-6 py-4">
                            <CompanyLogo
                              logoUrl={account.logo_url}
                              name={account.name}
                              className="h-8 w-8"
                            />
                          </TableCell>
                          <TableCell className="px-6 py-4">
                            <Link
                              href={bizminer_admin_organization_account_path(
                                organization.id,
                                account.id,
                              )}
                              className="text-sm font-medium text-primary hover:text-primary/80"
                            >
                              {account.name || "Unnamed Account"}
                            </Link>
                          </TableCell>
                          <TableCell className="px-6 py-4">
                            {account.gics_name ? (
                              <span className="text-sm text-muted-foreground">
                                {account.gics_name}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground italic">
                                Not set
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="px-6 py-4 text-right">
                            <Button asChild size="sm">
                              <Link
                                href={bizminer_admin_organization_account_path(
                                  organization.id,
                                  account.id,
                                )}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                View
                              </Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {pagy.last > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                      <span className="text-sm text-muted-foreground">
                        Showing {pagy.from} to {pagy.to} of {pagy.count}{" "}
                        accounts
                      </span>
                      <Pagination className="mx-0 w-auto">
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious
                              onClick={() =>
                                pagy.previous && handlePageChange(pagy.previous)
                              }
                              className={
                                !pagy.previous
                                  ? "pointer-events-none opacity-50"
                                  : "cursor-pointer"
                              }
                            />
                          </PaginationItem>
                          {Array.from(
                            { length: pagy.last },
                            (_, i) => i + 1,
                          ).map((page) => (
                            <PaginationItem key={page}>
                              <PaginationLink
                                onClick={() => handlePageChange(page)}
                                isActive={page === pagy.page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          ))}
                          <PaginationItem>
                            <PaginationNext
                              onClick={() =>
                                pagy.next && handlePageChange(pagy.next)
                              }
                              className={
                                !pagy.next
                                  ? "pointer-events-none opacity-50"
                                  : "cursor-pointer"
                              }
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        </div>
      </div>

      <CreateAccountDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        organizationId={organization.id}
        gicsTree={gics_tree}
        industries={industries}
      />
    </BizminerAdminLayout>
  );
}
